import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import useModalClose from 'hooks/useModalClose'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { IDeliveryDetailModalProps, IFuelDeliveryRecord } from 'types/fuelDeliveries.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { formatNumbersForDisplay } from 'utils/helpers'
import { formatGhs } from './Shared/fuelFormatters'
import {
  DeliveryClaimBanner,
  DeliveryDetailGrid,
  DeliveryDetailItem,
  DeliveryNoteWrap,
  DeliveryValueEm,
} from './DeliveryDetailModal.styled'

export const DELIVERY_VERIFICATION_META: {
  [k in IFuelDeliveryRecord['verificationStatus']]: { tone: IStatusPill['status']; label: string }
} = {
  VERIFIED: { tone: 'success', label: 'Verified' },
  SHORT: { tone: 'danger', label: 'Short' },
  UNVERIFIED: { tone: 'neutral', label: 'Unverified' },
}

const litres = (value: number | null): string =>
  value == null ? '—' : `${formatNumbersForDisplay(value)} L`

export const DeliveryDetailModal = ({ delivery, closeModal }: IDeliveryDetailModalProps) => {
  useModalClose(closeModal)
  const meta = DELIVERY_VERIFICATION_META[delivery.verificationStatus]
  const varianceColor =
    delivery.verificationStatus === 'SHORT'
      ? tokens.status.danger.fg
      : delivery.verificationStatus === 'VERIFIED'
        ? tokens.status.success.fg
        : tokens.text.muted

  return (
    <Modal black>
      <Flex column gap='0.5rem' width='100%'>
        <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
          <TitleText weight='bold'>{`Delivery — ${delivery.dateLabel} · ${delivery.tank}`}</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Flex center gap='0.5rem' wrap>
          <StatusPill status={meta.tone} dot={delivery.verificationStatus === 'SHORT'}>
            {meta.label}
          </StatusPill>
          <OtherText grey>{`${delivery.supplier} · ${delivery.fuelType}`}</OtherText>
        </Flex>
        <DeliveryDetailGrid>
          <DeliveryDetailItem>
            <OtherText grey>WAYBILL REF</OtherText>
            <DeliveryValueEm>{delivery.waybillRef || '—'}</DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>WAYBILL LITRES</OtherText>
            <DeliveryValueEm>{litres(delivery.waybillLitres)}</DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>TANK DIP BEFORE</OtherText>
            <DeliveryValueEm>{litres(delivery.tankDipBeforeL)}</DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>TANK DIP AFTER</OtherText>
            <DeliveryValueEm>{litres(delivery.tankDipAfterL)}</DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>MEASURED IN (DIP AFTER − BEFORE)</OtherText>
            <DeliveryValueEm>{litres(delivery.measuredInL)}</DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>VARIANCE VS WAYBILL</OtherText>
            <DeliveryValueEm $color={varianceColor}>
              {delivery.varianceL == null
                ? '—'
                : `${formatNumbersForDisplay(delivery.varianceL)} L (${delivery.variancePct}%)`}
            </DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>SEAL NUMBERS</OtherText>
            <DeliveryValueEm>
              {delivery.sealNumbers.length ? delivery.sealNumbers.join(', ') : '—'}
            </DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>SEALS</OtherText>
            {delivery.sealsIntact == null ? (
              <DeliveryValueEm>—</DeliveryValueEm>
            ) : (
              <StatusPill status={delivery.sealsIntact ? 'success' : 'danger'}>
                {delivery.sealsIntact ? 'Intact' : 'Mismatch'}
              </StatusPill>
            )}
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>PRICE / L — THIS DELIVERY</OtherText>
            <DeliveryValueEm>
              {delivery.pricePerL == null ? '—' : `GH₵ ${delivery.pricePerL.toFixed(2)}`}
            </DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>TOTAL</OtherText>
            <DeliveryValueEm>{formatGhs(delivery.costGhs)}</DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>VERIFIED BY</OtherText>
            <DeliveryValueEm>{delivery.verifiedBy || '—'}</DeliveryValueEm>
          </DeliveryDetailItem>
          <DeliveryDetailItem>
            <OtherText grey>RECEIVED INTO STOCK</OtherText>
            <DeliveryValueEm>{litres(delivery.liters)}</DeliveryValueEm>
          </DeliveryDetailItem>
        </DeliveryDetailGrid>
        {delivery.claimNote && (
          <DeliveryClaimBanner>
            <StatusPill status='danger' dot>
              Claim raised
            </StatusPill>
            <OtherText color={tokens.status.danger.fg}>{delivery.claimNote}</OtherText>
          </DeliveryClaimBanner>
        )}
        {delivery.verificationNote && (
          <DeliveryNoteWrap>
            <OtherText color={tokens.text.muted}>{delivery.verificationNote}</OtherText>
          </DeliveryNoteWrap>
        )}
      </Flex>
    </Modal>
  )
}
